import { Button, GoogleLogoIcon, Icon, MinervaLogoIcon } from "@minervaproject/fds";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { AlternateLink } from "../components/sso/alternate_link";
import { EmailLoginForm } from "../components/sso/email_login_form";
import { ErrorMessage } from "../components/sso/error_message";
import { OrgWrapper } from "../components/sso/org_wrapper";
import { Paragraph } from "../components/sso/paragraph";
import { Title } from "../components/sso/title";
import { LoginStore } from "../store/login";
import { OrgIcon } from "src/icons";
import { OrgSamlIdpStore, OrgSettingsStore, OrgStore } from "src/store";
import { getQueryParam } from "src/utils";

const iconMap = {
  google: GoogleLogoIcon,
  minerva: MinervaLogoIcon,
};

const LoginBody = () => {
  const isSingleOrgEnv = !!window.context?.isSingleOrgEnv;
  const { orgIdentifier } = useParams();
  const dispatch = useDispatch();

  const { error } = useSelector(LoginStore.sliceSelectors.select());
  const orgs = useSelector(OrgStore.selectors.selectAll());
  const org = isSingleOrgEnv ? orgs[0] : orgs.find((o) => o.short_name === orgIdentifier);
  const orgSettings = useSelector(OrgSettingsStore.selectors.find({ org_id: org?.id }));
  const samlIdps = useSelector(OrgSamlIdpStore.selectors.filter({ org_id: org?.id }));

  const showSSOAuth = !!orgSettings?.enable_login_via_sso;
  const emailLoginEnabled = !orgIdentifier || !!orgSettings?.enable_login_via_email_password;
  const passwordQueryParam = getQueryParam("password");
  const shouldShowEmailAuth = (emailLoginEnabled && !showSSOAuth) || passwordQueryParam;
  const [showEmailAuth, setShowEmailAuth] = useState(shouldShowEmailAuth);

  useEffect(() => {
    dispatch(LoginStore.actions.update({ contextualMessage: null }));
  }, [dispatch, showEmailAuth]);

  useEffect(() => {
    setShowEmailAuth(shouldShowEmailAuth);
  }, [shouldShowEmailAuth]);

  const next = getQueryParam("next") || "/app";
  const onSSOButtonClick = (idp) => () => {
    if (org?.disabled) {
      return dispatch(
        LoginStore.actions.setError("Access to this organization is not allowed at this time."),
      );
    }
    location.href = `/saml2/login/?idp=${idp.entity_id}&next=${next}`;
  };

  const handleAlternateLinkClick = (showEmail = true) => () => {
    dispatch(LoginStore.actions.clearError());
    setShowEmailAuth(showEmail);
  };

  if (!org) return null;

  if (showEmailAuth)
    return (
      <>
        <EmailLoginForm />
        {showSSOAuth ? (
          <AlternateLink
            text="Sign in with SSO Provider"
            onClick={() => handleAlternateLinkClick(false)}
            id="js-sso"
          />
        ) : null}
      </>
    );

  if (showSSOAuth)
    return (
      <>
        {samlIdps.map((idp) => {
          const ButtonIcon = iconMap[idp.icon] || Icon;
          return (
            <Button
              mb={2}
              fullWidth
              kind="secondary"
              icon={idp.icon === "org" ? <OrgIcon orgId={org?.id} /> : <ButtonIcon />}
              onClick={onSSOButtonClick(idp)}
              key={idp.id}
            >
              {`Continue with ${idp.continue_button_text}`}
            </Button>
          );
        })}
        {error ? <ErrorMessage msg={error} /> : null}
        {emailLoginEnabled ? (
          <AlternateLink
            onClick={() => handleAlternateLinkClick(true)}
            text="Use Email and Password"
            id="js-use-email"
          />
        ) : null}
      </>
    );

  return null;
};

const OrgLogin = () => {
  return (
    <OrgWrapper>
      <Title text="Sign in to Forum" />
      <LoginBody />
    </OrgWrapper>
  );
};

const OrgLoginPassword = () => {
  const { contextualMessage } = useSelector(LoginStore.selectors.select());
  return (
    <OrgWrapper>
      <Title text="Sign in to Forum" />
      <Paragraph text={contextualMessage} />
      <EmailLoginForm />
    </OrgWrapper>
  );
};

export { OrgLogin, OrgLoginPassword };
